import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  getIssueCreate,
  getIssueEdit,
  getIssueProjects,
  getIssuesBulk,
  getIssueTrackers,
  getIssueTrackersBulk,
  getParentIssueQuery,
  saveIssue,
  saveIssueBulk,
  saveIssueEdit,
  uploadFile,
} from '../../../packages/common/api';
import FormField from '../../../packages/common/shared-ui/src/components/FormField/FormField';
import { ButtonsTypes } from '../../../packages/common/shared-ui/src/enums/commonEnums';
import AttachIcon from '../../../packages/common/shared-ui/src/icons/AttachIcon';
import ChevronIcon from '../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteFileIcon from '../../../packages/common/shared-ui/src/icons/DeleteFileIcon';
import {
  formatFields,
  saveFormat,
} from '../../../packages/common/utils/fieldHelper';
import { formValidation } from '../../../packages/common/utils/formValidation';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import CommonPage from '../CommonPage/CommonPage';
import { store } from '../app/redux';
import IssueFiles from '../issuePage/components/IssueFiles/IssueFiles';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Grid)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: issueLink
const StyledIssueLink = styled(Link)(theme => ({
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const IssueCreateEdit = () => {
  const getLocalString = useTranslation();
  const pageParams = useParams();
  const navigate = useNavigate();
  const IssueState = { CREATE: 'create', EDIT: 'edit', BULK_EDIT: 'bulk_edit' };
  const PageActions = {
    SAVE: 'save',
    SAVE_AND_CONTINUE: 'save_and_continue',
    CANCEL: 'cancel',
  };
  const [pageTitle, setPageTitle] = useState('');
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [trackers, setTrackers] = useState([]);
  const [issue, setIssue] = useState({});
  const [issueAllFields, setIssueAllFields] = useState([]);
  const [parentIssueOptions, setParentIssueOptions] = useState([]);
  const [fieldsColumnsBreak, setFieldColumnsBreak] = useState({
    breakCount: 0,
    breakState: false,
  });
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [projectsParams, setProjectsParams] = useState({
    paramsChecked: false,
    params: {},
  });
  const [trackerParams, setTrackerParams] = useState({
    paramsChecked: false,
    params: {},
  });
  const [getParams, setGetParams] = useState({
    paramsChecked: false,
    params: {},
  });
  const [issueState, setIssueState] = useState(null);
  const [issues, setIssues] = useState([]);
  const [defaultProject, setDefaultProject] = useState(null);
  const [defaultTracker, setDefaultTracker] = useState(null);
  const [formError, setFormError] = useState(false);
  const [issueRemovedFields, setIssueRemovedFields] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [issueNotes, setIssueNotes] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [projectField, setProjectField] = useState(null);
  const [trackerField, setTrackerField] = useState(null);

  const issueFormState = useRef(
    new Map([
      [
        IssueState.CREATE,
        {
          getResponse: getIssueCreate,
          saveResponse: saveIssue,
          pageTitle: 'issue_new_title',
        },
      ],
      [
        IssueState.EDIT,
        {
          getResponse: getIssueEdit,
          saveResponse: saveIssueEdit,
          pageTitle: 'issue_edit_title',
        },
      ],
      [
        IssueState.BULK_EDIT,
        {
          getResponse: getIssuesBulk,
          saveResponse: saveIssueBulk,
          pageTitle: 'issue_edit_bulk_title',
        },
      ],
    ]),
  );
  const urlParams = useRef(new URLSearchParams(location.search));
  const state = store.getState();

  useEffect(() => {
    location.pathname.includes(IssueState.CREATE) &&
      setIssueState(IssueState.CREATE);
    pageParams.copyId && setIssueState(IssueState.CREATE);
    location.pathname.includes(IssueState.EDIT) &&
      setIssueState(IssueState.EDIT);
    location.pathname.includes(IssueState.BULK_EDIT) &&
      setIssueState(IssueState.BULK_EDIT);
  }, [
    IssueState.BULK_EDIT,
    IssueState.CREATE,
    IssueState.EDIT,
    location.pathname,
    pageParams.copyId,
  ]);

  useEffect(() => {
    const params = {};
    urlParams.current.get('projectId') &&
      (params.checked = urlParams.current.get('projectId'));
    pageParams.parentId && (params.parentIssue = pageParams.parentId);
    setProjectsParams({ paramsChecked: true, params: params });
  }, []);
  useEffect(() => {
    const params = {};
    pageParams.parentId && (params.parentIssue = pageParams.parentId);
    urlParams.current.get('trackerId') &&
      (params.checked = urlParams.current.get('trackerId'));
    selectedProject && (params.project = selectedProject.valueId);
    defaultProject && (params.project = defaultProject.valueId);
    setTrackerParams({ paramsChecked: true, params: params });
  }, [selectedProject, defaultProject]);

  useEffect(() => {
    const params = {};
    selectedProject && (params.project = selectedProject.valueId);
    selectedTracker && (params.tracker = selectedTracker.valueId);
    issueState === IssueState.BULK_EDIT &&
      (params.issues = urlParams.current.get('issues').replaceAll(',', '||'));
    issueState === IssueState.CREATE &&
      pageParams.parentId &&
      (params.parentIssue = pageParams.parentId);
    pageParams.copyId && (params.copy = pageParams.copyId);
    if (issueState) {
      if (issueState === IssueState.CREATE) {
        selectedProject &&
          selectedTracker &&
          setGetParams({ paramsChecked: true, params: params });
      } else {
        setGetParams({ paramsChecked: true, params: params });
      }
    }
  }, [
    IssueState.CREATE,
    IssueState.EDIT,
    issueState,
    pageParams,
    pageParams.copyId,
    pageParams.id,
    pageParams.parentId,
    selectedProject,
    selectedTracker,
  ]);

  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    async function getProjects() {
      const { data: projects } = await getIssueProjects(projectsParams.params);
      if (!ignore) {
        return projects;
      }
    }
    if (projectsParams.paramsChecked) {
      getProjects()
        .then(res => {
          setProjects(res);
          const checkedItem = res.find(item => item.isChecked);
          checkedItem && setSelectedProject(checkedItem);
          let project = {
            fieldId: 'project',
            fieldName: getLocalString('IssueCreateEdit', 'labels', 'project'),
            valueId: null,
            valueName: null,
            fieldDefinition: {
              fieldId: 'project',
              fieldName: getLocalString('IssueCreateEdit', 'labels', 'project'),
              fieldFormat: 'list',
              isMultiple: false,
              isRequired: issueState !== IssueState.BULK_EDIT,
              isReadonly: !!pageParams.parentId,
              values: res,
            },
          };
          checkedItem &&
            (project = {
              ...project,
              valueName: checkedItem,
              valueId: checkedItem,
            });
          setTrackerField({
            fieldId: 'tracker',
            fieldName: getLocalString('IssueCreateEdit', 'labels', 'tracker'),
            valueId: null,
            valueName: null,
            fieldDefinition: {
              fieldId: 'tracker',
              fieldName: getLocalString('IssueCreateEdit', 'labels', 'tracker'),
              fieldFormat: 'list',
              isMultiple: false,
              isRequired: issueState !== IssueState.BULK_EDIT,
              isReadonly: false,
              values: [],
            },
          });
          setProjectField(project);
        })
        .catch(error => handleIssueError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [projectsParams]);

  useEffect(() => {
    let ignore = false;
    async function getTrackers() {
      const { data: trackers } = await getIssueTrackersBulk({
        issues: urlParams.current.get('issues').replaceAll(',', '||'),
      });
      if (!ignore) {
        return trackers;
      }
    }

    if (issueState === IssueState.BULK_EDIT) {
      getTrackers()
        .then(res => {
          setTrackers(res);
          setTrackerField({
            ...trackerField,
            fieldDefinition: {
              ...trackerField.fieldDefinition,
              values: res,
            },
          });
        })
        .catch(error => handleIssueError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [issueState]);

  useEffect(() => {
    let ignore = false;
    async function getTrackers() {
      const { data: trackers } = await getIssueTrackers(trackerParams.params);
      if (!ignore) {
        return trackers;
      }
    }
    if ((defaultProject || selectedProject) && trackerParams.paramsChecked) {
      getTrackers()
        .then(res => {
          setTrackers(res);
          const checkedItem = res.find(item => item.isChecked);
          checkedItem && setSelectedTracker(checkedItem);
          if (
            selectedTracker &&
            res.data.filter(
              tracker => tracker.valueId === selectedTracker.valueId,
            ).length === 0
          ) {
            setSelectedTracker(null);
            setIssueAllFields([]);
            setFieldColumnsBreak({ breakCount: 0, breakState: false });
          }
          setTrackers(res);
          let tracker = {
            ...trackerField,
            fieldDefinition: {
              ...trackerField.fieldDefinition,
              values: res,
            },
          };
          checkedItem &&
            (tracker = {
              ...tracker,
              valueName: checkedItem,
              valueId: checkedItem,
            });
          setTrackerField(tracker);
        })
        .catch(error => handleIssueError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [trackerParams.params]);

  useEffect(() => {
    let ignore = false;
    async function getIssueData() {
      setLoading(true);
      const response = await issueFormState.current
        .get(issueState)
        .getResponse(pageParams.id, getParams.params);
      if (!ignore) {
        return response;
      }
    }
    if (getParams.paramsChecked) {
      getIssueData()
        .then(res => {
          const { data: issueData } = res;
          let fields = {};
          setIssueNotes({
            fieldId: 'notes',
            fieldName: getLocalString('IssueCreateEdit', 'labels', 'note'),
            valueId: null,
            valueName: null,
            fieldDefinition: {
              fieldId: 'notes',
              fieldName: getLocalString('IssueCreateEdit', 'labels', 'note'),
              fieldFormat: 'text',
              isMutiple: false,
              isRequired: false,
            },
          });
          issueData.issue.standards &&
            (fields.standards = issueData.issue.standards);
          issueData.issue.customs && (fields.customs = issueData.issue.customs);
          setIssueAllFields(formatFields(fields));
          setIssue({ ...issueData.issue });
          issueState === IssueState.EDIT &&
            issueData.issue.hasOwnProperty('notes') &&
            setShowNotes(true);
          issueState === IssueState.BULK_EDIT && setShowNotes(true);
          issueState === IssueState.BULK_EDIT && setIssues(issueData.issues);
          issueState === IssueState.EDIT &&
            setIssues(issueData.issue.hierarchy);
          issueData.services &&
            issueData.services.removed &&
            setIssueRemovedFields(
              issueData.services.removed.standards.concat(
                issueData.services.removed.customs,
              ),
            );
          //issueState === IssueState.CREATE && setPageTitle(intl.formatMessage({ id: 'issue_new_title' }));
          issueState === IssueState.EDIT &&
            setPageTitle(
              `${issueData.issue.hierarchy[0].subject} #${issueData.issue.hierarchy[0].issueId}`,
            );
          //issueState === IssueState.BULK_EDIT && setPageTitle(intl.formatMessage({ id: 'issue_edit_bulk_title' }));
          setLoading(false);
        })
        .catch(error => handleIssueError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [getParams]);
  useEffect(() => {
    if (
      issue.projectId &&
      issueState === IssueState.EDIT &&
      projects.length > 0
    ) {
      const field = projects.find(
        item => Number(item.valueId) === issue.projectId,
      );
      setDefaultProject(field);
      setProjectField(prev => ({
        ...prev,
        valueName: field,
        valueId: field,
      }));
    }
  }, [IssueState.EDIT, issue.projectId, issueState, projects, defaultProject]);
  useEffect(() => {
    if (
      issue.trackerId &&
      issueState === IssueState.EDIT &&
      trackers.length > 0
    ) {
      const field = trackers.find(
        item => Number(item.valueId) === issue.trackerId,
      );
      setDefaultTracker(field);
      setTrackerField(prev => ({
        ...prev,
        valueName: field,
        valueId: field,
      }));
    }
  }, [IssueState.EDIT, issueState, issue.trackerId, trackers]);
  useEffect(() => {
    if (issueAllFields.length > 20) {
      setFieldColumnsBreak({
        breakCount: parseInt((issueAllFields.length / 2).toString()),
        breakState: true,
      });
    } else {
      setFieldColumnsBreak({ breakCount: 0, breakState: false });
    }
  }, [issueAllFields]);

  const clearIssue = useCallback(() => {
    setSelectedTracker(null);
    !urlParams.current.get('projectId') && setTrackers([]);
    !urlParams.current.get('projectId') && setSelectedProject(null);
    setIssue({});
    setIssueAllFields([]);
  }, []);
  const handlePageNavigation = useCallback(
    (action, id) => {
      let url = '/issues';
      const list = urlParams.current.get('list');
      if (action === PageActions.SAVE) {
        url = `/issues/${id}`;
        issueState === IssueState.BULK_EDIT && (url = `/issues`);
      }
      if (action === PageActions.SAVE_AND_CONTINUE) {
        url = `/issues/create`;
        pageParams.parentId &&
          (url = `/issues/${pageParams.parentId}/create?projectId=${urlParams.current.get('projectId')}`);
        pageParams.id &&
          (url = `/issues/${pageParams.id}/copy?trackerId=${urlParams.current.get(
            'trackerId',
          )}&projectId=${urlParams.current.get('projectId')}`);
        /*        if (state.user.project) {
          url = `/issues/create?projectId=${urlParams.current.get('projectId')}&project=${
            state.user.project.redmineId
          }`;
          pageParams.parentId &&
            (url = `/issues/${pageParams.parentId}/create?projectId=${urlParams.current.get('projectId')}&project=${
              state.user.project.redmineId
            }`);
          pageParams.id &&
            (url = `/issues/${pageParams.id}/copy?trackerId=${urlParams.current.get(
              'trackerId',
            )}&projectId=${urlParams.current.get('projectId')}&project=${state.user.project.redmineId}`);
        }*/
        list !== null && (url = url + '&list');
        !pageParams.id && clearIssue();
      }
      if (action === PageActions.CANCEL) {
        pageParams.id &&
          urlParams.current.get('list') === null &&
          (url = `/issues/${pageParams.id}`);
        pageParams.parentId && (url = `/issues/${pageParams.parentId}`);
        state.user.project &&
          (url = url + `?project=${state.user.project.redmineId}`);
        navigate(url);
      }
      if (urlParams.current.get('agile') !== null) {
        url = `/agile?project=${urlParams.current.get('project')}`;
      }
      pageParams.projectId && (url = `/projects/${pageParams.projectId}${url}`);
      navigate(url);
    },
    [
      IssueState.BULK_EDIT,
      PageActions.CANCEL,
      PageActions.SAVE,
      PageActions.SAVE_AND_CONTINUE,
      clearIssue,
      issueState,
      navigate,
      pageParams.id,
      state.user.project,
      pageParams.parentId,
      pageParams.projectId,
    ],
  );

  const handleFormField = useCallback((data, field) => {
    if (field.fieldId === 'project' || field.fieldId === 'tracker') {
      if (field.fieldId === 'project') {
        setSelectedProject(data.valueId);
        setProjectField(prev => ({
          ...prev,
          valueName: data.valueId,
          valueId: data.valueId,
        }));
      }
      if (field.fieldId === 'tracker') {
        setSelectedTracker(data.valueId);
        setTrackerField(prev => ({
          ...prev,
          valueName: data.valueId,
          valueId: data.valueId,
        }));
      }
    } else {
      setIssueAllFields(prevFields => {
        const pos = prevFields.map(e => e.fieldId).indexOf(field.fieldId);
        const fieldValue = {
          ...field,
          ...data,
        };
        return [
          ...prevFields.slice(0, pos),
          fieldValue,
          ...prevFields.slice(pos + 1),
        ];
      });
    }
  }, []);
  const handleValidation = useCallback(() => {
    if (issueState === IssueState.BULK_EDIT && !selectedTracker) {
      return false;
    }
    const fields =
      issueState === IssueState.BULK_EDIT
        ? issueAllFields.filter(item => item.custom)
        : issueAllFields;
    const validationResult = formValidation(fields);
    setIssueAllFields(validationResult.fields);
    setErrors(validationResult.errors);
    return validationResult.error;
  }, [IssueState.BULK_EDIT, issueAllFields, issueState, selectedTracker]);

  const saveForm = useCallback(
    async action => {
      const validation = handleValidation();
      if (validation) {
        return;
      }
      let changedField;
      let filledStandardFields;
      let filledCustomsFields;
      let requiredFieldsStandard;
      let requiredFieldsCustom;
      let changedFieldsStandard;
      let changedFieldsCustom;
      let resultStandardArray;
      let resultCustomsArray;
      changedField = issueAllFields.filter(field => field.changed);
      filledStandardFields = issueAllFields.filter(
        field =>
          field.valueName !== null && field.valueName !== '' && !field.custom,
      );
      filledCustomsFields = issueAllFields.filter(
        field =>
          field.valueName !== null && field.valueName !== '' && field.custom,
      );
      issueState === IssueState.CREATE &&
        (changedField = issueAllFields.filter(
          field => field.changed && !field.fieldDefinition.isRequired,
        ));
      changedFieldsStandard = changedField.filter(field => !field.custom);
      changedFieldsCustom = changedField.filter(field => field.custom);
      issueState === IssueState.CREATE &&
        (requiredFieldsStandard = issueAllFields.filter(
          field => field.fieldDefinition.isRequired && !field.custom,
        ));
      issueState === IssueState.CREATE &&
        (requiredFieldsCustom = issueAllFields.filter(
          field => field.fieldDefinition.isRequired && field.custom,
        ));
      resultStandardArray = [...changedFieldsStandard];
      resultCustomsArray = [...changedFieldsCustom];
      issueState === IssueState.CREATE &&
        (resultStandardArray = [
          ...requiredFieldsStandard,
          ...changedFieldsStandard,
        ]);
      issueState === IssueState.CREATE &&
        (resultCustomsArray = [
          ...requiredFieldsCustom,
          ...changedFieldsCustom,
        ]);
      issue.copiedFrom && (resultStandardArray = [...filledStandardFields]);
      issue.copiedFrom && (resultCustomsArray = [...filledCustomsFields]);

      const fields = saveFormat({
        standards: resultStandardArray,
        customs: resultCustomsArray,
      });
      let data = {
        ...fields,
        attachments: attachments,
      };
      selectedProject && (data.projectId = Number(selectedProject.valueId));
      selectedTracker &&
        (data.trackerId = selectedTracker
          ? Number(selectedTracker.valueId)
          : issue.trackerId);
      issue.copiedFrom && (data.copiedFrom = issue.copiedFrom);
      issueAllFields.find(
        field =>
          !field.changed &&
          field.fieldDefinition.fieldFormat === 'issue_parent',
      ) &&
        pageParams.parentId &&
        (data.parentIssue = Number(pageParams.parentId));
      issueNotes.valueName && (data.notes = issueNotes.valueName);
      issueState === IssueState.BULK_EDIT &&
        (data = { ids: issues.map(item => item.issueId), issue: { ...data } });
      setLoading(true);
      try {
        return await issueFormState.current
          .get(issueState)
          .saveResponse(pageParams.id, data);
      } catch (error) {
        setLoading(false);
        handleIssueError(error.response);
      }
    },

    [
      handleValidation,
      issueAllFields,
      issueState,
      IssueState.CREATE,
      IssueState.BULK_EDIT,
      attachments,
      selectedProject,
      selectedTracker,
      issue.trackerId,
      issue.copiedFrom,
      pageParams.parentId,
      pageParams.id,
      issueNotes,
      issues,
      handlePageNavigation,
      handleIssueError,
    ],
  );

  const handleFileUpload = useCallback(async event => {
    setLoadingUpload(true);
    try {
      Promise.all(
        [...event.target.files].map(
          async file => await uploadFile(file, file.name),
        ),
      ).then(data => {
        const files = data.map((item, index) => ({
          token: item.data.upload.token,
          fileName: [...event.target.files][index].name,
          contentType: [...event.target.files][index].type,
        }));
        setAttachments(prev => [...prev, ...files]);
        setLoadingUpload(false);
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleDeleteIssueFile = useCallback(
    index => {
      const newArray = attachments.filter((file, i) => i !== index);
      setAttachments(newArray);
    },
    [attachments],
  );

  const handleParentIssueChange = useCallback(
    async value => {
      setSearchLoading(true);
      let params = {
        search: value,
      };
      issueState === IssueState.BULK_EDIT &&
        (params.issues = issues.map(item => item.issueId).join('||'));
      issueState === IssueState.EDIT && (params.issues = pageParams.id);
      issueState === IssueState.CREATE &&
        selectedProject &&
        (params.project = selectedProject.valueId);
      issueState === IssueState.CREATE &&
        selectedTracker &&
        (params.tracker = selectedTracker.valueId);
      try {
        const response = await getParentIssueQuery(params);
        if (response) {
          setParentIssueOptions(response.data);
          setSearchLoading(false);
        }
        //      }
      } catch (error) {
        console.error('ERROR WITH EDIT', error);
      }
    },
    [
      issueState,
      IssueState.BULK_EDIT,
      IssueState.EDIT,
      IssueState.CREATE,
      issues,
      pageParams.id,
      selectedProject,
      selectedTracker,
    ],
  );

  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.SAVE:
          saveForm(type).then(res => {
            if (res) {
              const { data: response } = res;
              const id =
                issueState === IssueState.CREATE ? response : pageParams.id;
              handlePageNavigation(type, id);
              setLoading(false);
            }
          });
          break;
        case ButtonsTypes.SAVE_AND_CONTINUE:
          saveForm(type).then(res => {
            if (res) {
              const { data: response } = res;
              const id =
                issueState === IssueState.CREATE ? response : pageParams.id;
              handlePageNavigation(type, id);
              setLoading(false);
            }
          });
          break;
        case ButtonsTypes.CANCEL:
          handlePageNavigation(type, pageParams.id);
      }
    },
    [
      IssueState.CREATE,
      handlePageNavigation,
      issueState,
      pageParams.id,
      saveForm,
    ],
  );

  return (
    <>
      {issueState && (
        <CommonPage
          loading={loading}
          buttonEvent={handleButtonEvent}
          disableSave={issueAllFields.length === 0}
          component={'IssueCreateEdit'}
          state={issueState}
        >
          {issues && issues.length > 0 && (
            <Grid container direction={'column'} sx={{ mb: '20px', mt: '4px' }}>
              {issues.map((item, index) => {
                return (
                  <Grid key={index}>
                    <Typography variant='h5'>
                      {issueState === IssueState.BULK_EDIT && (
                        <StyledIssueLink
                          to={`/issues/${item?.issueId}`}
                          target={'_blank'}
                          style={{ color: '#082253' }}
                        >
                          {item?.issueName}
                        </StyledIssueLink>
                      )}
                      {issueState === IssueState.EDIT && (
                        <>
                          <Box
                            component={'span'}
                            sx={{
                              color: '#082253',
                            }}
                          >
                            {`${item.trackerName} #${item?.issueId}:`}
                          </Box>
                          <Box
                            component={'span'}
                            sx={{
                              color: '#212346',
                              marginLeft: '4px',
                            }}
                          >
                            {item.subject}
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          )}
          {errors.length > 0 && (
            <Box>
              {errors.map((error, index) => (
                <Typography
                  key={index}
                  variant='h3'
                  sx={{
                    color: '#E03737',
                  }}
                >
                  {getLocalString('errors', error.message)}
                </Typography>
              ))}
            </Box>
          )}
          {issueRemovedFields.length > 0 && (
            <Box>
              <Typography
                variant='h3'
                sx={{
                  color: '#E03737',
                }}
              >
                {getLocalString('messages', 'fieldsChange')}
                {issueRemovedFields.map(item => item).join(', ')}
              </Typography>
            </Box>
          )}
          <Grid
            container
            spacing={5}
            sx={{ marginTop: issueState === IssueState.CREATE ? '20px' : 0 }}
          >
            <Grid
              size={{
                lg: 6,
              }}
            >
              <StyledBlockWrapper
                container
                direction={'column'}
                sx={{
                  padding: '0 32px 16px 32px',
                }}
              >
                {projectField && (
                  <FormField
                    field={projectField}
                    fieldEvent={handleFormField}
                  ></FormField>
                )}
                {trackerField && (
                  <FormField
                    field={trackerField}
                    fieldEvent={handleFormField}
                  ></FormField>
                )}
                {fieldsColumnsBreak.breakState
                  ? issueAllFields
                      .filter(item => item.fieldId !== 'description')
                      .slice(0, fieldsColumnsBreak.breakCount)
                      .map((field, index) => (
                        <FormField
                          key={index}
                          field={field}
                          fieldEvent={handleFormField}
                          changeSearchInput={handleParentIssueChange}
                          searchLoading={searchLoading}
                          searchFieldOptions={parentIssueOptions}
                          bulk={issueState === IssueState.BULK_EDIT}
                        ></FormField>
                      ))
                  : issueAllFields
                      .filter(item => item.fieldId !== 'description')
                      .map((field, index) => (
                        <FormField
                          key={index}
                          field={field}
                          fieldEvent={handleFormField}
                          changeSearchInput={handleParentIssueChange}
                          searchLoading={searchLoading}
                          searchFieldOptions={parentIssueOptions}
                          bulk={issueState === IssueState.BULK_EDIT}
                        ></FormField>
                      ))}
                {issueAllFields.length > 0 && (
                  <>
                    <FormControl
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginTop: '16px',
                      }}
                    >
                      <StyledSelectLabel sx={{ width: '200px' }}>
                        <Typography
                          variant='h5'
                          sx={{
                            color: '#41424E',
                          }}
                        >
                          {getLocalString('labels', 'files')}
                        </Typography>
                      </StyledSelectLabel>
                      <Grid
                        container
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          variant='defaultGreyPrimary'
                          component='label'
                          sx={{ width: '115px', padding: '6px 16px' }}
                        >
                          <Typography variant='h5'>
                            {getLocalString('labels', 'selectFile')}
                          </Typography>
                          <input
                            hidden
                            multiple
                            type='file'
                            onClick={event => (event.currentTarget.value = '')}
                            onChange={event => handleFileUpload(event)}
                          />
                        </Button>
                        {loadingUpload && (
                          <Grid
                            container
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '30px',
                              height: '30px',
                              marginLeft: '10px',
                            }}
                          >
                            <CircularProgress size={20} color='secondary' />
                          </Grid>
                        )}
                      </Grid>
                    </FormControl>
                    {issueState === IssueState.EDIT && (
                      <IssueFiles viewOnly></IssueFiles>
                    )}
                    {attachments.length > 0 && (
                      <Grid
                        container
                        direction={'column'}
                        sx={{
                          marginLeft: '200px',
                        }}
                      >
                        {attachments.map((file, index) => (
                          <Grid
                            key={index}
                            sx={{
                              marginTop: '8px',
                              lineHeight: '20px',
                            }}
                          >
                            <Grid container>
                              <Grid>
                                <Box
                                  component={'span'}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <AttachIcon />
                                  <Typography
                                    variant='h4'
                                    sx={{
                                      color: '#7174AC',
                                      marginLeft: '4px',
                                    }}
                                  >
                                    {file.fileName}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid
                                sx={{ display: 'flex', marginLeft: '13px' }}
                              >
                                <IconButton
                                  sx={{ padding: '0', marginLeft: '4px' }}
                                  onClick={() => handleDeleteIssueFile(index)}
                                >
                                  <DeleteFileIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </>
                )}
              </StyledBlockWrapper>
              {issueAllFields.length > 0 && !fieldsColumnsBreak.breakState && (
                <>
                  {issueAllFields.find(
                    item => item.fieldId === 'description',
                  ) && (
                    <StyledBlockWrapper
                      sx={{
                        padding: '16px 32px',
                        marginTop: '16px',
                      }}
                    >
                      <FormField
                        field={issueAllFields.find(
                          item => item.fieldId === 'description',
                        )}
                        fieldEvent={handleFormField}
                        bulk={issueState === IssueState.BULK_EDIT}
                      ></FormField>
                    </StyledBlockWrapper>
                  )}
                  {showNotes && issueNotes && (
                    <StyledBlockWrapper
                      sx={{
                        padding: '16px 32px',
                        marginTop: '16px',
                      }}
                    >
                      <FormField
                        field={issueNotes}
                        fieldEvent={event =>
                          setIssueNotes({
                            ...issueNotes,
                            valueId: event.valueId,
                            valueName: event.valueName,
                          })
                        }
                        bulk={issueState === IssueState.BULK_EDIT}
                      ></FormField>
                    </StyledBlockWrapper>
                  )}
                </>
              )}
            </Grid>
            {fieldsColumnsBreak.breakState && (
              <Grid
                size={{
                  lg: 6,
                }}
              >
                <StyledBlockWrapper
                  container
                  direction={'column'}
                  sx={{
                    padding: '0 32px 16px 32px',
                  }}
                >
                  {issueAllFields
                    .filter(item => item.fieldId !== 'description')
                    .slice(fieldsColumnsBreak.breakCount)
                    .map((field, index) => (
                      <FormField
                        key={index}
                        field={field}
                        fieldEvent={handleFormField}
                        bulk={issueState === IssueState.BULK_EDIT}
                      ></FormField>
                    ))}
                </StyledBlockWrapper>
                {issueAllFields.length > 0 && fieldsColumnsBreak.breakState && (
                  <>
                    {issueAllFields.find(
                      item => item.fieldId === 'description',
                    ) && (
                      <StyledBlockWrapper
                        sx={{
                          padding: '16px 32px',
                          marginTop: '16px',
                        }}
                      >
                        <FormField
                          field={issueAllFields.find(
                            item => item.fieldId === 'description',
                          )}
                          fieldEvent={handleFormField}
                          bulk={issueState === IssueState.BULK_EDIT}
                        ></FormField>
                      </StyledBlockWrapper>
                    )}
                    {showNotes && issueNotes && (
                      <StyledBlockWrapper
                        sx={{
                          padding: '16px 32px',
                          marginTop: '16px',
                        }}
                      >
                        <FormField
                          field={issueNotes}
                          fieldEvent={event =>
                            setIssueNotes({
                              ...issueNotes,
                              valueId: event.valueId,
                              valueName: event.valueName,
                            })
                          }
                          bulk={issueState === IssueState.BULK_EDIT}
                        ></FormField>
                      </StyledBlockWrapper>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </CommonPage>
      )}
    </>
  );
};

export default IssueCreateEdit;
